import { Clock, FlaskConical, Globe, Newspaper, Palette, Trophy } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * Ollie Learns topic catalogue.
 *
 * Each topic ships its canonical accent + a soft companion used in chip
 * backgrounds, and an icon from lucide-react (stroke-width 2, rounded
 * caps, consistent with the rest of the app). The six topics below are
 * the ones named in the landing spec §7c.
 */
export type LearnsTopicId = 'history' | 'science' | 'world' | 'today' | 'arts' | 'sports';

export interface LearnsTopic {
  id: LearnsTopicId;
  label: string;
  icon: LucideIcon;
  /** Canonical topic colour — used for chip eyebrows, active chip bg, topic pills. */
  accent: string;
  /** Soft tint — used behind inactive chip icons and as a pairing colour. */
  soft: string;
}

export const LEARNS_TOPICS: LearnsTopic[] = [
  { id: 'history', label: 'History', icon: Clock, accent: '#B85A1E', soft: '#FBE8D0' },
  { id: 'science', label: 'Science', icon: FlaskConical, accent: '#2E8A8A', soft: '#DCEDE9' },
  { id: 'world', label: 'World', icon: Globe, accent: '#6B8FB8', soft: '#DDE7F2' },
  { id: 'today', label: 'Today in the News', icon: Newspaper, accent: '#8B5BB8', soft: '#E6DCF0' },
  { id: 'arts', label: 'Arts & Culture', icon: Palette, accent: '#C85A7A', soft: '#F5DDE3' },
  { id: 'sports', label: 'Sports', icon: Trophy, accent: '#D4952A', soft: '#F5E6C5' },
];

/** Lookup helper — component code can pull a topic by id without repeating the .find. */
export const topicById = (id: LearnsTopicId): LearnsTopic =>
  LEARNS_TOPICS.find((t) => t.id === id)!;

/**
 * Placeholder stripe palettes used by the featured card overlay and
 * every grid card. Stripes are rendered as
 * `repeating-linear-gradient(135deg, colorA 0 14px, colorB 14px 28px)`.
 */
export type LearnsTone = 'warm' | 'sand' | 'teal' | 'dusk';

export const LEARNS_TONE_PALETTES: Record<LearnsTone, { a: string; b: string }> = {
  warm: { a: '#F6E2C4', b: '#ECD1A8' },
  sand: { a: '#EADFC7', b: '#D9C9A9' },
  teal: { a: '#D4E8E3', b: '#BFD9D3' },
  dusk: { a: '#D9C2C2', b: '#BFA1A1' },
};

export const toneBackgroundImage = (tone: LearnsTone) => {
  const { a, b } = LEARNS_TONE_PALETTES[tone];
  return `repeating-linear-gradient(135deg, ${a} 0 14px, ${b} 14px 28px)`;
};

/**
 * The article catalogue lives in the database now (see
 * `server/prisma/seed-articles.ts`). Fetch via `articlesService.list()`
 * or `articlesService.featured()` in components.
 */
